import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const REVIEW_STATUSES = ["submitted", "pending", "processing"];
const APPROVED_STATUSES = ["approved", "verified"];
const STATUS_FILTERS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const normalize = (value) => String(value || "").toLowerCase();

const formatDate = (value) => {
  if (!value) return "N/A";
  if (typeof value === "number") return new Date(value).toLocaleString("en-GB");
  if (value.toDate) return value.toDate().toLocaleString("en-GB");
  return String(value);
};

const getSortValue = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value.toDate) return value.toDate().getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getRequestSortValue = (user) =>
  getSortValue(
    user.kyc?.submittedAt ||
      user.kyc?.updatedAt ||
      user.kycSubmittedAt ||
      user.kycUpdatedAt
  );

const getStatusValue = (user) =>
  normalize(user.kycStatus || user.kyc?.status || "not_submitted");

const getStatusLabel = (status) => {
  if (!status) return "Not submitted";
  if (status === "verified") return "Verified";
  if (status === "submitted") return "Submitted";
  if (status === "pending") return "Pending";
  if (status === "processing") return "Processing";
  if (status === "approved") return "Approved";
  if (status === "rejected") return "Rejected";
  if (status === "not_submitted") return "Not submitted";
  return status;
};

const getStatusClass = (status) => {
  if (APPROVED_STATUSES.includes(status)) return "success";
  if (status === "rejected") return "danger";
  if (REVIEW_STATUSES.includes(status)) return "warning";
  return "muted";
};

const matchesStatusFilter = (status, filter) => {
  if (filter === "pending") return REVIEW_STATUSES.includes(status);
  if (filter === "approved") return APPROVED_STATUSES.includes(status);
  if (filter === "rejected") return status === "rejected";
  return false;
};

const getFilterStatuses = (filter) => {
  if (filter === "pending") return REVIEW_STATUSES;
  if (filter === "approved") return APPROVED_STATUSES;
  if (filter === "rejected") return ["rejected"];
  return REVIEW_STATUSES;
};

const KycApprovals = () => {
  const { user: currentUser, role: currentRole } = useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [docPreview, setDocPreview] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadRequests = async () => {
      setLoading(true);
      setSelectedUser(null);
      try {
        const statuses = getFilterStatuses(statusFilter);
        const userCollection = collection(db, "users");
        const queries = [
          query(userCollection, where("kycStatus", "in", statuses), limit(200)),
          query(
            userCollection,
            where("kyc.status", "in", statuses),
            limit(200)
          ),
        ];
        const snaps = await Promise.all(queries.map((item) => getDocs(item)));
        if (!isActive) return;
        const merged = new Map();
        snaps.forEach((snap) => {
          snap.docs.forEach((docSnap) => {
            merged.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        });
        const list = Array.from(merged.values());
        list.sort((a, b) => getRequestSortValue(b) - getRequestSortValue(a));
        setRequests(list);
      } catch (error) {
        console.error("Failed to load KYC requests", error);
        if (isActive) {
          setRequests([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      isActive = false;
    };
  }, [statusFilter]);

  const filteredRequests = useMemo(() => {
    const queryText = normalize(search.trim());
    return requests.filter((item) => {
      const status = getStatusValue(item);
      if (!matchesStatusFilter(status, statusFilter)) return false;
      if (!queryText) return true;
      return (
        normalize(item.username).includes(queryText) ||
        normalize(item.email).includes(queryText) ||
        normalize(item.phoneNumber).includes(queryText) ||
        normalize(item.id).includes(queryText)
      );
    });
  }, [requests, search, statusFilter]);

  useEffect(() => {
    if (!selectedUser) {
      setRejectionReason("");
      return;
    }
    setRejectionReason(
      selectedUser.kyc?.rejectionReason || selectedUser.kycRejectionReason || ""
    );
  }, [selectedUser]);

  useEffect(() => {
    setDocPreview(null);
  }, [selectedUser]);

  useEffect(() => {
    if (
      selectedUser &&
      !filteredRequests.some((item) => item.id === selectedUser.id)
    ) {
      setSelectedUser(null);
    }
  }, [filteredRequests, selectedUser]);

  const updateKycStatus = async (nextStatus) => {
    if (!selectedUser || !currentUser) return;
    const normalizedStatus = normalize(nextStatus);
    const reason =
      normalizedStatus === "rejected" ? rejectionReason.trim() : "";
    if (normalizedStatus === "rejected" && !reason) return;

    setSaving(true);
    try {
      const userRef = doc(db, "users", selectedUser.id);
      const isVerified = APPROVED_STATUSES.includes(normalizedStatus);
      const payload = {
        kycStatus: nextStatus,
        kycVerified: isVerified,
        "kyc.status": nextStatus,
        "kyc.updatedAt": serverTimestamp(),
        "kyc.updatedBy": currentUser.uid,
        "kyc.updatedByRole": currentRole || null,
      };
      if (normalizedStatus === "rejected") {
        payload["kyc.rejectionReason"] = reason;
        payload.kycRejectionReason = reason;
      } else {
        payload["kyc.rejectionReason"] = null;
        payload.kycRejectionReason = null;
      }
      if (isVerified) {
        payload["kyc.verifiedAt"] = serverTimestamp();
      }

      await updateDoc(userRef, payload);

      const updatedKyc = {
        ...(selectedUser.kyc || {}),
        status: nextStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
        updatedByRole: currentRole || null,
        rejectionReason: normalizedStatus === "rejected" ? reason : null,
        verifiedAt: isVerified ? new Date() : selectedUser.kyc?.verifiedAt,
      };
      const updatedUser = {
        ...selectedUser,
        kycStatus: nextStatus,
        kycVerified: isVerified,
        kyc: updatedKyc,
        kycRejectionReason: normalizedStatus === "rejected" ? reason : null,
      };

      setRequests((prev) =>
        prev.map((item) => (item.id === selectedUser.id ? updatedUser : item))
      );
      setSelectedUser(updatedUser);
    } catch (error) {
      console.error("Failed to update KYC status", error);
    } finally {
      setSaving(false);
    }
  };

  const selectedStatus = selectedUser ? getStatusValue(selectedUser) : "";
  const kyc = selectedUser?.kyc || {};
  const docs = kyc.documents || {};
  const bank = kyc.bankDetails || {};
  const submittedAt = kyc.submittedAt || selectedUser?.kycSubmittedAt;
  const updatedAt = kyc.updatedAt || selectedUser?.kycUpdatedAt;
  const updatedBy = kyc.updatedBy || selectedUser?.kycUpdatedBy;
  const previewUrl = docPreview?.url || "";
  const isImagePreview = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(
    previewUrl
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>KYC approvals</h1>
          <p>Review submitted documents and verify accounts.</p>
        </div>
        <div className="page-actions">
          <input
            className="search-input"
            type="search"
            placeholder="Search by name, email, phone, or UID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="manage-tabs">
        {STATUS_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`manage-tab ${
              statusFilter === item.value ? "active" : ""
            }`}
            onClick={() => setStatusFilter(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid-2">
        <div className="card table-card">
          <div className="card-header">
            <h3>KYC requests</h3>
            <span className="chip">{filteredRequests.length} results</span>
          </div>
          {loading ? (
            <div className="empty-inline">Loading KYC requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="empty-inline">No KYC requests found.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((item) => {
                    const statusValue = getStatusValue(item);
                    const submitted =
                      item.kyc?.submittedAt ||
                      item.kycSubmittedAt ||
                      item.kyc?.updatedAt ||
                      item.kycUpdatedAt;
                    return (
                      <tr
                        key={item.id}
                        className={
                          selectedUser?.id === item.id ? "active-row" : ""
                        }
                        onClick={() => setSelectedUser(item)}
                      >
                        <td>
                          <div className="table-title">
                            {item.username || "Unknown"}
                          </div>
                          <div className="table-subtitle">{item.id}</div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${getStatusClass(
                              statusValue
                            )}`}
                          >
                            {getStatusLabel(statusValue)}
                          </span>
                        </td>
                        <td>{formatDate(submitted)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card detail-card">
          <div className="card-header">
            <h3>KYC details</h3>
          </div>
          {!selectedUser ? (
            <div className="empty-inline">Select a request to review.</div>
          ) : (
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">UID</div>
                <div className="detail-value">{selectedUser.id}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Username</div>
                <div className="detail-value">
                  {selectedUser.username || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Email</div>
                <div className="detail-value">{selectedUser.email || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Phone</div>
                <div className="detail-value">
                  {selectedUser.phoneNumber || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Status</div>
                <div className="detail-value">
                  <span
                    className={`status-pill ${getStatusClass(selectedStatus)}`}
                  >
                    {getStatusLabel(selectedStatus)}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Submitted</div>
                <div className="detail-value">{formatDate(submittedAt)}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Updated</div>
                <div className="detail-value">{formatDate(updatedAt)}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Updated by</div>
                <div className="detail-value">{updatedBy || "N/A"}</div>
              </div>

              {selectedStatus === "rejected" && rejectionReason ? (
                <div className="detail-row">
                  <div className="detail-label">Rejection reason</div>
                  <div className="detail-value">{rejectionReason}</div>
                </div>
              ) : null}

              <div className="divider" />

              <div className="detail-row">
                <div className="detail-label">Aadhaar number</div>
                <div className="detail-value">
                  {docs.aadharNumber || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">PAN number</div>
                <div className="detail-value">{docs.panNumber || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Aadhaar doc</div>
                <div className="detail-value">
                  {docs.aadharUrl ? (
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() =>
                        setDocPreview({
                          title: "Aadhaar document",
                          url: docs.aadharUrl,
                        })
                      }
                    >
                      View
                    </button>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">PAN doc</div>
                <div className="detail-value">
                  {docs.panUrl ? (
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() =>
                        setDocPreview({
                          title: "PAN document",
                          url: docs.panUrl,
                        })
                      }
                    >
                      View
                    </button>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>

              <div className="divider" />

              <div className="detail-row">
                <div className="detail-label">Account holder</div>
                <div className="detail-value">
                  {bank.accountHolderName || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Bank name</div>
                <div className="detail-value">{bank.bankName || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Account number</div>
                <div className="detail-value">
                  {bank.accountNumber || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">IFSC</div>
                <div className="detail-value">{bank.ifscCode || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">UPI ID</div>
                <div className="detail-value">{bank.upiId || "N/A"}</div>
              </div>

              <div className="divider" />

              <label className="field">
                <span>Rejection reason</span>
                <input
                  type="text"
                  placeholder="Reason for rejection"
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                />
              </label>

              <div className="inline-actions">
                <button
                  className="button primary"
                  type="button"
                  onClick={() => updateKycStatus("approved")}
                  disabled={saving || APPROVED_STATUSES.includes(selectedStatus)}
                >
                  {saving ? "Saving..." : "Approve"}
                </button>
                <button
                  className="button danger"
                  type="button"
                  onClick={() => updateKycStatus("rejected")}
                  disabled={
                    saving ||
                    selectedStatus === "rejected" ||
                    !rejectionReason.trim()
                  }
                >
                  Reject
                </button>
              </div>
              <div className="helper-text">
                Approving sets the user as verified for withdrawals.
              </div>
            </div>
          )}
        </div>
      </div>
      {docPreview ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setDocPreview(null)}
        >
          <div
            className="modal doc-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3>{docPreview.title}</h3>
                <div className="helper-text">Document preview</div>
              </div>
              <button
                className="button ghost small"
                type="button"
                onClick={() => setDocPreview(null)}
              >
                Close
              </button>
            </div>
            <div className="modal-body">
              <div className="doc-preview">
                {isImagePreview ? (
                  <img src={previewUrl} alt={docPreview.title} />
                ) : (
                  <iframe title={docPreview.title} src={previewUrl} />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default KycApprovals;
